import { Component, OnInit } from '@angular/core';
import { UserService } from '../service/user.service';
import { HttpService } from '../service/http.service';
import { MenuItem, MessageService, SelectItem } from 'primeng/api';
import { LoginService } from '../security/login.service';
import { FaviconService } from '../favicon.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GmailMessage } from '../gmail-message';
import { UserForBackend } from '../user-for-backend';
import { environment } from 'src/environments/environment';
import { EmailSignature } from '../email-signature';
import { Language } from '../language';
import { forkJoin } from 'rxjs';
import { ToastMessageService } from '../toast-message.service';


@Component({
  selector: 'app-account-setting',
  templateUrl: './account-setting.component.html',
  styleUrls: ['./account-setting.component.css']
})
export class AccountSettingComponent implements OnInit {

  username!: string;
  authCode!: string;
  gmailForm!: FormGroup;
  selectedUser!: UserForBackend;
  signatureForm!: FormGroup | null;
  navigationItems: MenuItem[] = [];
  activeItem: MenuItem | null = null;
  selectedLanguageId: number | null = null;
  selectedLanguage: Language| null = null;
  emailSignatures: EmailSignature[] = [];
  signatureCreationForm!: FormGroup | null;
  postingSignature = false;
  languages: Language[] = [];
  languageItems: SelectItem[] = [];
  //language for main language of user
  selectedMainLanguage!: Language;
  selectedTestingEmailBody!: string;
  userEmail!: string;
  hasDefaultLeadFilter = false;

  constructor(
    private userService: UserService,
    private httpService: HttpService,
    private messageService: MessageService,
    private loginService: LoginService,
    private favicon: FaviconService,
    private builder: FormBuilder,
    private toastService: ToastMessageService
  ) {
    this.loginService.activatedNavigationComponent = true;
    this.favicon.sbms();
  }

  ngOnInit() {
    this.username = this.userService.userName!;
    this.initForm();
    forkJoin({
      signatures: this.httpService.getEmailSignaturesByUser(),
      languages: this.httpService.getLanguages(),
      user: this.httpService.getUserInfoByCookie(),
      userEmail: this.httpService.getEmailAddress()
    }).subscribe(({ signatures, languages, user, userEmail }) => {
      this.emailSignatures = signatures;
      this.userEmail = userEmail;
      for (let language of languages) {
        //push language for tabmenu
        this.navigationItems.push({
          id: language.id.toString(),
          label: language.name,
          command: () => {
            this.selectedLanguageId = language.id!;
            this.selectedLanguage = language!;
            this.signatureCreationForm = null;
            this.initSignatureForm(language);
          }
        });
        //push language for dropdown item
        this.languageItems.push({
          label: language.name,
          value: language
        });
      }
      this.languageItems.push({
        label: "None",
        value: null
      });
      this.selectedUser = user;
      this.selectedMainLanguage = user.mainLanguage!;
      this.selectedLanguageId = languages[0].id;
      this.selectedLanguage = languages[0];
      this.activeItem = this.navigationItems[0];
      this.initSignatureForm(languages[0]);
    });
  }

  initForm() {
    this.gmailForm = this.builder.group({
      sendTo: ["", Validators.email],
      subject: ["", Validators.required],
      messageBody: ["", Validators.minLength(3)]
    });
  }

  initSignatureForm(language: Language) {
    let languageMatch = false;
    for (let signature of this.emailSignatures) {
      if (signature.language.id == language.id) {
        this.signatureForm = this.builder.group({
          body: signature.body,
          id: signature.id,
          language: signature.language
        });
        languageMatch = true;
      }
    }
    if (!languageMatch) {
      this.signatureForm = null;
    }
  }

  openOauth2ConsentScreen() {
    this.httpService.getRedirectUrlForGoogleOauth2ConsentScreen().subscribe(res => {
      window.location.href = res;
    }, error => this.toastService.addError("Error", error.error.message));
  }

  addSuccess(summary: string, detail: string) {
    this.messageService.add({ severity: 'success', summary: summary, detail: detail });
  }

  addError(summary: string, detail: string) {
    this.messageService.add({ severity: 'error', summary: summary, detail: detail });
  }

  sendMail() {
    console.log(this.gmailForm);
    this.httpService.sendEmailByGoogle(new GmailMessage(
      [this.gmailForm.value.sendTo],
      this.gmailForm.value.subject,
      this.gmailForm.value.messageBody
    )).subscribe(() => {
      this.addSuccess("Success", "Your message is sent to " + this.gmailForm.value.sendTo.trim());
      this.initForm();
    }, error => {
      console.log(error);
      this.addError("Error", error.error.message);
    });
  }

  updateEmailSignature() {
    let signature = new EmailSignature(
      this.signatureForm!.value.id,
      this.signatureForm!.value.body,
      this.signatureForm!.value.language
    );
    this.httpService.updateEmailSignature(signature).subscribe(() => {
      this.addSuccess("Success", "Email signature is updated");
      this.httpService.getEmailSignaturesByUser().subscribe(res => {
        this.emailSignatures = res;
        this.initSignatureForm(this.selectedLanguage!);
      });
    }, error => {
      this.addError("Error", error.message);
    });
  }

  activateNewSignatureForm() {
    this.signatureCreationForm = this.builder.group({
      body: [null, Validators.required],
      language: this.selectedLanguage
    });
  }

  postEmailSignature() {
    if (this.signatureCreationForm!.invalid) {
      this.toastService.addError("Error", "Form value is invalid");
      return;
    }
    this.postingSignature = true;
    let signature = new EmailSignature(
      null,
      this.signatureCreationForm!.value.body,
      this.signatureCreationForm!.value.language
    );
    this.httpService.postEmailSignature(signature).subscribe(() => {
      this.signatureCreationForm = null;
      this.postingSignature = false;
      this.toastService.addSuccess("Success", "New signature added");
      this.httpService.getEmailSignaturesByUser().subscribe(res => {
        this.emailSignatures = res;
        this.initSignatureForm(this.selectedLanguage!);
      });
    }, error => {
      this.postingSignature = false;
      this.toastService.addError("Error", error.message);
    });
  }

  updateMainLanguage() {
    this.httpService.updateUserMainLanguage(this.selectedMainLanguage).subscribe(() => {
      this.selectedUser.mainLanguage = this.selectedMainLanguage;
      this.toastService.addSuccess("Success", "Main language updated");
    }, error => this.toastService.addError("Error", error.message));
  }

  sendTestEmailForNewOauth2() {
    if (this.selectedTestingEmailBody == "" || !this.selectedTestingEmailBody) {
      this.toastService.addError("Error", "Please write something in the email body");
      return;
    }
    let gmailMessage = new GmailMessage([this.userEmail],
      "Testing OAuth2",
      this.selectedTestingEmailBody);
    this.httpService.postEmailWithNewFlow(gmailMessage).subscribe(() => {
      this.toastService.addSuccess("Success", "Email sent without problem");
    }, error => {
      this.toastService.addError("Error", error.error.message);
    });
  }

  onDefaultLeadFilterChange(){
    this.httpService.updateDefaultLeadFilter(this.selectedUser.hasDefaultLeadFilter).subscribe(() => {
      this.toastService.addSuccess("Success", "Update successfull");
    }, error => {
      this.toastService.addError("Error", error.error.message);
    })
  }

}
